import {
  setupMultiApiClient,
  createTokenStorage,
  createHeaderRule,
  getEndpointsForEnv,
} from "@oneapp/data";
import { createTestsApiConfig } from "./buildEndpoints";

/** Response from login-with-OTP. Persisted as accountsData. */
export interface AccountsData {
  access_token: string;
  refresh_token: string;
  is_user_existing: boolean;
  unified_accounts: {
    unified_account_id: string;
    first_name: string;
    last_name: string;
    grade?: number;
    user_account_photo?: string | null;
  }[];
}

/** Profile item within unified_account_details.profiles. */
export interface ProfileItem {
  uam_id?: number;
  admission_id?: string;
  crn_id?: string;
  branch_id?: string;
  tenant_id?: string;
  vertical_id?: number;
  is_default_profile?: boolean;
  is_temp_password?: boolean;
  tenant_code?: string;
  exams?: { examId: number; name: string }[];
  [key: string]: unknown;
}

/** Response from profiles API. Persisted as profilesData. */
export interface ProfilesData {
  access_token?: string;
  refresh_token?: string;
  response?: string;
  unified_account_details: {
    unified_account_id?: string;
    profiles?: ProfileItem[];
    user_account_details?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

/** Single program from enrolled-programs API. Persisted as enrolledPrograms. */
export interface EnrolledProgram {
  program_id?: string;
  program_name?: string;
  gradeId?: number;
  examId?: number;
  gradeName?: string;
  examName?: string;
  batch?: string | null;
  tenant_id?: string;
  subtenant_id?: number;
  vertical_id?: number;
  subjects?: unknown[];
  mdsExamName?: string;
  mdsExamId?: number;
  mdsGradeName?: string;
  mdsGradeId?: number;
  duration_in_weeks?: number;
  mdsOfferingName?: string;
  mdsOfferingId?: number;
  programOfferingIds?: number[];
  isDefault?: boolean;
  [key: string]: unknown;
}

export interface ModuleDataDto {
  accessToken: string | null;
  refreshToken: string | null;
  accountsData: AccountsData | null;
  profilesData: ProfilesData | null;
  enrolledPrograms: EnrolledProgram[] | null;
  selectedProgram?: EnrolledProgram | null;
  studentTimeZone?: string | null;
  env?: string;
}

export const defaultModuleDataDto: ModuleDataDto = {
  accessToken: null,
  refreshToken: null,
  accountsData: null,
  profilesData: null,
  enrolledPrograms: null,
  studentTimeZone: null,
  env: "production",
};

const noOpTokenStorage = createTokenStorage({
  getAccessToken: () => null,
  getRefreshToken: () => null,
  setTokens: () => {},
  clearTokens: () => {},
});

function buildHeaderRules(dataDto: ModuleDataDto = defaultModuleDataDto) {
  const profile = dataDto.profilesData?.unified_account_details?.profiles?.[0];
  const primaryProgram =
    dataDto.selectedProgram ??
    dataDto.enrolledPrograms?.find((program) => program.isDefault) ??
    dataDto.enrolledPrograms?.[0];
  const getTenant = (): string =>
    (profile?.tenant_code as string) ?? "infinitylearn";
  const getDeviceId = (): string =>
    (typeof localStorage !== "undefined" && localStorage.getItem("deviceIp")) ||
    "127.0.0.1";
  const getUserId = (): string | null =>
    profile?.uam_id != null ? String(profile.uam_id) : null;
  const getUserDetails = (): {
    subTenant?: string | number;
    tenantId?: string | number;
  } | null =>
    profile
      ? {
          tenantId: profile.tenant_id,
          subTenant: primaryProgram?.subtenant_id,
        }
      : null;
  const getAccessToken = (): string | null => dataDto.accessToken ?? null;
  const getRefreshToken = (): string | null => dataDto.refreshToken ?? null;

  return [
    createHeaderRule({
      pattern: /one_asm_api|one_asm_asset_api|proctoringUrl|geojs/,
      remove: [
        "X-Tenant",
        "ot-tenant",
        "X-Platform",
        "X-DeviceId",
        "Authorization",
      ],
      priority: 100,
    }),
    createHeaderRule({
      pattern: /api\.oneapp\.com/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /infinitylearn\.com|devinfinitylearn\.in/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /selflearnapi/,
      add: {
        subTenantId: () => getUserDetails()?.subTenant?.toString() ?? null,
        tenantId: () => getUserDetails()?.tenantId?.toString() ?? null,
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /lcapi\.oneapp\.com/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
        userid: () => getUserId(),
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /api\/selflearn\/getMapping/,
      remove: ["X-Tenant", "ot-tenant", "X-Platform", "X-DeviceId"],
      priority: 100,
    }),
    createHeaderRule({
      pattern: /myactivity\.devinfinitylearn\.in/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
        accesstoken: () => getAccessToken(),
        refreshtoken: () => getRefreshToken(),
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /s3\.ap-south-1\.amazonaws\.com/,
      remove: [
        "Authorization",
        "X-Tenant",
        "ot-tenant",
        "X-Platform",
        "X-DeviceId",
      ],
      priority: 100,
    }),
    createHeaderRule({
      pattern:
        /assets\.infinitylearn\.com\/scorerevamp\/homepage\/json\/il_score_about_test\.json/,
      remove: [
        "Authorization",
        "X-Tenant",
        "ot-tenant",
        "X-Platform",
        "X-DeviceId",
      ],
      priority: 100,
    }),
  ];
}

export function initializeApi(
  env: string = "production",
  dataDto: ModuleDataDto = defaultModuleDataDto,
): void {
  const apiEndpoints = getEndpointsForEnv(env);

  setupMultiApiClient({
    apiConfig: createTestsApiConfig(apiEndpoints),
    tokenStorage: noOpTokenStorage,
    refreshTokenFn: async () => {
      throw new Error("Tests module: no refresh token configured");
    },
    onAuthFailure: () => {},
    onError: () => {},
    enableLogging: env === "development" || env === "staging",
    retry: {
      count: 2,
      delay: 1000,
      statusCodes: [408, 500, 502, 503, 504],
    },
    timeout: 30000,
    crossTab: { enabled: false },
    headerRules: buildHeaderRules(dataDto),
  });
}
